import { MENTAL_HEALTH_PLAN } from "constants/callConstants";
import { addUkDays, displayUkDate, toUkDateString, toUkDisplayDate, ukToday } from "helpers/ukDate";

/**
 * What a mental-health answer change will do, worked out in the panel so a super admin can see it on
 * the confirm step BEFORE saving: the answer before and after, and what happens to the reminder email.
 *
 * The server is the authority - it computes the send moment itself and decides the reminder's state
 * (ConsultationMentalHealthReviewsService.planReminder) - and these rules mirror it:
 *   - a follow-up is emailed 2 days BEFORE its date, a repeat plan 21 days AFTER the consultation,
 *     both at 10:00 UK time (BST or GMT, whichever applies that day);
 *   - the same reminder that is waiting or already sent is left as it is; an email whose send time
 *     has already passed is never scheduled (the answer is saved, no email); anything else is armed,
 *     and a reminder that was waiting is cancelled first.
 */

export const SEND_TIME_TEXT = "10:00 UK time";
export const FOLLOW_UP_LEAD_DAYS = 2;
export const REPEAT_PLAN_DELAY_DAYS = 21;

const PLAN_TEXT = {
    [MENTAL_HEALTH_PLAN.followUp]: "Follow-up needed",
    [MENTAL_HEALTH_PLAN.repeatPlan]: "Join repeat prescription plan",
};

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/**
 * @param {string} ukDate YYYY-MM-DD
 * @returns {string} e.g. "Monday 02-11-2026"
 */
export function dayText(ukDate) {
    if (!ukDate) {
        return "";
    }
    const weekday = WEEKDAYS[new Date(`${ukDate}T12:00:00.000Z`).getUTCDay()];
    return `${weekday} ${displayUkDate(ukDate)}`;
}

/**
 * The answer as one line.
 *
 * @param {object|null} state {isMentalHealth, plan, followUpDueAt} - a review, or a payload
 * @returns {string}
 */
export function describeAnswer(state) {
    if (!state) {
        return "Not asked";
    }
    if (!state.isMentalHealth) {
        return "Not a mental health consultation";
    }
    const plan = PLAN_TEXT[state.plan] || "no plan";
    const due = state.followUpDueAt ? `, due ${toUkDisplayDate(state.followUpDueAt)}` : "";
    return `Mental health consultation: ${plan}${due}`;
}

/**
 * The UK day the one email for a plan goes out.
 *
 * @param {object} params
 * @param {number|null} params.plan
 * @param {string} [params.followUpDate] YYYY-MM-DD
 * @param {string} [params.consultationDate] YYYY-MM-DD
 * @returns {string|null} YYYY-MM-DD, or null when no email is owed
 */
export function emailDay({ plan, followUpDate, consultationDate }) {
    if (plan === MENTAL_HEALTH_PLAN.followUp && followUpDate) {
        return addUkDays(-FOLLOW_UP_LEAD_DAYS, followUpDate);
    }
    if (plan === MENTAL_HEALTH_PLAN.repeatPlan && consultationDate) {
        return addUkDays(REPEAT_PLAN_DELAY_DAYS, consultationDate);
    }
    return null;
}

/**
 * The hour of the day it is now in the UK (0-23).
 *
 * @param {Date} now
 * @returns {number}
 */
export function ukHour(now) {
    const hour = new Intl.DateTimeFormat("en-GB", { hour: "numeric", hourCycle: "h23", timeZone: "Europe/London" }).format(now);
    return Number(hour);
}

/**
 * Everything the confirm step says.
 *
 * @param {object|null} review the answer as the API returned it (null when never asked)
 * @param {object} payload what is about to be saved: {isMentalHealth, plan, followUpDueAt}
 * @param {object} context {consultationDate: YYYY-MM-DD, today: YYYY-MM-DD, hourNow: number}
 * @returns {{from: string, to: string, email: string, notes: string[]}}
 */
export function describeChange(review, payload, context = {}) {
    const today = context.today || ukToday();
    const hourNow = context.hourNow === undefined ? ukHour(new Date()) : context.hourNow;
    const plan = payload.isMentalHealth ? (payload.plan || null) : null;
    const newDay = emailDay({
        plan,
        followUpDate: payload.followUpDueAt ? toUkDateString(payload.followUpDueAt) : null,
        consultationDate: context.consultationDate,
    });
    const oldDay = review && review.remindAt ? toUkDateString(review.remindAt) : null;
    const waiting = Boolean(review && review.remindersActive && review.remindAt);
    const sent = Boolean(review && review.lastRemindedAt);
    const sameReminder = Boolean(review) && review.plan === plan && Boolean(newDay) && newDay === oldDay;

    const notes = [];
    let email;

    if (!newDay) {
        email = "No email to the patient.";
        if (waiting) {
            notes.push(`The reminder email scheduled for ${dayText(oldDay)} will be cancelled.`);
        }
    } else if (sameReminder && waiting) {
        email = `Unchanged: still ${dayText(newDay)} at ${SEND_TIME_TEXT}.`;
    } else if (sameReminder && sent) {
        email = `No new email: the reminder was already sent on ${toUkDisplayDate(review.lastRemindedAt)}.`;
    } else if (newDay < today || (newDay === today && hourNow >= 10)) {
        // The server never schedules an email whose time has passed: the answer is saved, no email.
        email = `No email: its send time (${dayText(newDay)} at ${SEND_TIME_TEXT}) has already passed.`;
        if (waiting) {
            notes.push(`The reminder email scheduled for ${dayText(oldDay)} will be cancelled.`);
        }
    } else {
        email = `${dayText(newDay)} at ${SEND_TIME_TEXT}.`;
        if (waiting) {
            notes.push(`The reminder email scheduled for ${dayText(oldDay)} will be cancelled.`);
        }
        if (sent) {
            notes.push(`A reminder was already sent on ${toUkDisplayDate(review.lastRemindedAt)}. This schedules another.`);
        }
    }

    return { from: describeAnswer(review), to: describeAnswer(Object.assign({}, payload, { plan })), email, notes };
}

/**
 * One history entry's state - the answer and what had happened to its reminder.
 *
 * @param {object|null} state a snapshot from GET .../mental-health-review/changes
 * @returns {string}
 */
export function describeHistoryState(state) {
    if (!state) {
        return "Not asked";
    }
    const answer = describeAnswer(state);
    if (!state.remindAt) {
        return answer;
    }
    if (state.lastRemindedAt) {
        return `${answer} (email sent ${toUkDisplayDate(state.lastRemindedAt)})`;
    }
    if (state.remindersActive) {
        return `${answer} (email ${toUkDisplayDate(state.remindAt)}, ${SEND_TIME_TEXT})`;
    }
    return `${answer} (email cancelled)`;
}
